import { Component, Vue, Prop, Ref, Watch } from 'vue-property-decorator';
import { Camera } from '@/models/camera';
import { Project } from '@/models/project';
import { Photo } from '@/models/photo';
import { Album } from '@/models/album';
import { Getter, State, Action } from 'vuex-class';
import { Storage } from 'aws-amplify';
import VueSlider from 'vue-slider-component';
import axios from 'axios';
// @ts-ignore no d.ts
import Datepicker from 'vuejs-datepicker';
// @ts-ignore no d.ts
import { en, fr } from 'vuejs-datepicker/dist/locale';
import { Validations } from 'vuelidate-property-decorators';
import { minLength, required } from 'vuelidate/lib/validators';
import { AlbumCreateRequest } from '@/services/albums/albums.interfaces';
import EditShareModal from '../editsharemodal/editsharemodal.component.vue';
import ImportPhotosModal from '../importphotos/importphotos.component.vue';
import S3Image from '../s3-image/S3Image.vue';
import BlurModal from '@/components/blurmodal/blurmodal.component.vue';
import { BTooltip } from 'bootstrap-vue';
import { Logger } from 'aws-amplify';
import { format } from 'date-fns';
import { User } from '@/models/user';
import { cameraService } from '@/services';
const logger = new Logger('Gallery');

interface S3GetResponse {
  Body: Blob | ArrayBuffer;
  // Include other properties if necessary
}

@Component({
  components: {
    VueSlider,
    Datepicker,
    EditShareModal,
    ImportPhotosModal,
    S3Image,
    BlurModal,
    BTooltip,
  },
})
export default class Gallery extends Vue {
  public currentImageKey: Photo | null = null;
  public sliderValue: Photo | null = null;
  public sliderIsDragging = false;
  public isSliderFirstItem = false;
  public isSliderLastItem = true;
  public switchBeforeAfter = false;
  public createdAlbum = '';
  public selectedAlbum = '';
  private photoFavAlbum = true;
  private pickedDate: Date | null = null;
  public isTakingPicture = false;
  public isDescativate = false;
  public isMirroring = false;
  public mirroringSuccess = false;

  @Validations()
  private validations = {
    selectedAlbum: { required },
    createdAlbum: { required, minLength: minLength(3) },
  };

  @Prop({ required: true, type: Object }) private readonly camera!: Camera;
  @Prop({ required: true, type: Object }) private readonly project!: Project;
  @Prop({ required: true, type: Boolean }) private readonly newPhotoAvailable!: boolean;
  @Prop({ required: true }) readonly isPremium!: boolean;
  @Prop({ required: true }) readonly isGuest!: boolean;
  @Prop({ type: Array, default: () => [] }) readonly selectedPhotos!: Photo[];
  @Prop({ type: Boolean, default: false }) readonly useIA!: boolean;
  @Prop({ type: Boolean, default: false }) readonly useSecurityMode!: boolean;
  @Prop({ type: Boolean, default: false })
  @Prop({ type: Boolean, default: false })
  readonly hideFavorite!: boolean;
  @Prop({ type: Boolean, default: false }) readonly hideGoutte!: boolean;
  @Prop({ type: Boolean, default: false }) readonly hideUpload!: boolean;
  @Prop({ type: Boolean, default: false }) readonly hideCompareBtn!: boolean;
  @Prop({ type: Boolean, default: false }) readonly hideFullscreen!: boolean;
  readonly preferMediumInFullScreen!: boolean;

  @Ref('slider') private readonly slider!: VueSlider;

  @State('locale', { namespace: 'profile' }) private locale!: string;
  @State('user', { namespace: 'profile' }) private user!: User;
  @State('isLoading', { namespace: 'photo' }) private readonly isLoadingPhoto!: boolean;
  @State('photos', { namespace: 'photo' }) private readonly photosStore?: Photo[];
  @State('photosByCameraId', { namespace: 'photo' }) private readonly photosByCameraId!: {
    [cameraId: string]: Photo[];
  };
  @Getter('onlyPhoto', { namespace: 'photo' }) private onlyPhoto!: Photo[];

  @State('albums', { namespace: 'album' }) private readonly albums!: Album[];
  @Action('createNewAlbum', { namespace: 'album' }) private readonly createNewAlbum!: (
    cameraCreateRequest: AlbumCreateRequest
  ) => Promise<Album[]>;
  @Action('addPhoto', { namespace: 'album' }) private readonly addPhoto!: (payload: {
    albumId: string;
    photoId: string;
  }) => Promise<void>;

  public zoomLevel = 1;

  get filteredPhotos(): Photo[] {
    return this.photosStore?.filter((photo) => photo.cameraId === this.camera.id) || [];
  }

  public get imagesKey(): Photo[] {
    const photos = this.photosByCameraId[this.camera.id] || [];
    // Trier les photos
    const sortedPhotos = photos.slice().sort((photo1, photo2) => {
      return new Date(photo1.createdAt).getTime() - new Date(photo2.createdAt).getTime();
    });

    // Appliquer le filtre jetlag si nécessaire
    if (this.isGuest && this.project.jetlag) {
      return this.filterImagesWithinLagDuration(sortedPhotos, this.project.lagduration);
    } else {
      return sortedPhotos;
    }
  }

  get computedImagePath(): string {
    let basePath = '';

    if (this.camera.blur && !this.zoom) {
      // Sur la page IA et pas en mode plein écran, on utilise toujours la même image (par exemple, mediumKey)
      basePath = this.currentImageKey ? this.currentImageKey.mediumKey : '';
    } else {
      // Pour les autres cas, on adapte en fonction du zoomLevel
      if (this.zoomLevel > 1.8) {
        basePath = this.currentImageKey ? this.currentImageKey.key : '';
      } else {
        basePath = this.currentImageKey ? this.currentImageKey.mediumKey : '';
      }
    }

    if (this.useIA && !this.zoom) {
      // Sur la page IA et pas en mode plein écran, on utilise toujours la même image (par exemple, mediumKey)
      basePath = this.currentImageKey ? this.currentImageKey.mediumKey : '';
    } else {
      // Pour les autres cas, on adapte en fonction du zoomLevel
      if (this.zoomLevel > 1.8) {
        basePath = this.currentImageKey ? this.currentImageKey.key : '';
      } else {
        basePath = this.currentImageKey ? this.currentImageKey.mediumKey : '';
      }
    }

    // Vérifie si useIA est activé et modifie le chemin si nécessaire
    // Vérifie si useIA ou useSecurityMode est activé
    if ((this.useIA || this.useSecurityMode) && basePath) {
      const segments = basePath.split('/');

      let jpgIndex = -1;
      for (let i = segments.length - 1; i >= 0; i--) {
        if (segments[i].toLowerCase().includes('.jpg')) {
          jpgIndex = i;
          break;
        }
      }

      if (jpgIndex !== -1) {
        // Insérer 'IA' et éventuellement 'Segmentation'
        segments.splice(jpgIndex, 0, 'IA');
        if (this.useSecurityMode) {
          segments.splice(jpgIndex + 1, 0, 'Segmentation');
        }
        basePath = segments.join('/');
      } else {
        basePath += '/IA';
        if (this.useSecurityMode) {
          basePath += '/Segmentation';
        }
      }
    }

    return basePath;
  }

  public get zoom(): boolean {
    return this.$route.query.theater !== undefined;
  }

  public set zoom(set: boolean) {
    if (set && this.$route.query.theater === undefined) {
      this.$router.push({ path: this.$route.path, query: { ...this.$route.query, theater: '' } });
    } else if (this.$route.query.theater === '') {
      this.$router.push({ path: this.$route.path, query: { ...this.$route.query, theater: undefined } });
    }
  }

  public get dateFormat(): string {
    return this.locale === 'en' ? 'MM / DD / YYYY' : 'DD / MM / YYYY';
  }

  public get disabledDates(): unknown {
    const startDate = this.imagesKey[0] ? this.imagesKey[0].createdAt : null;
    const endDate = this.imagesKey[this.imagesKey.length - 1]
      ? this.imagesKey[this.imagesKey.length - 1].createdAt
      : null;
    if (startDate && endDate) {
      const to = new Date(startDate);
      to.setHours(to.getHours() + to.getTimezoneOffset() / 60);
      to.setHours(0);
      const from = new Date(endDate);
      from.setHours(to.getHours() + from.getTimezoneOffset() / 60);
      from.setDate(from.getDate() + 1);
      return {
        ranges: [
          {
            from: new Date(-8640000000000000), // Min date
            to,
          },
          {
            from,
            to: new Date(8640000000000000), // Max date
          },
        ],
      };
    } else {
      return {};
    }
  }

  public get datepickerFormat(): string {
    return this.locale === 'en' ? 'yyyy-dd-MM' : 'yyyy-MM-dd';
  }

  public get datepickerLocal(): unknown {
    return this.locale === 'en' ? en : fr;
  }

  public get isAdmin(): boolean {
    return (
      this.user !== undefined &&
      (this.project.owner.id === this.user.id || this.project.administratorId.includes(this.user.id))
    );
  }

  private get computedFullScreenImagePath(): string {
    // Détermine le chemin de base en fonction du niveau de zoom
    let basePath = '';
    if (!this.useIA && this.zoom) {
      basePath = this.currentImageKey ? this.currentImageKey.key : '';
    } else if (this.zoomLevel > 1.8) {
      basePath = this.currentImageKey ? this.currentImageKey.mediumKey : '';
    } else {
      basePath = this.currentImageKey ? this.currentImageKey.mediumKey : '';
    }

    // Appliquez la logique 'useIA' si nécessaire
    if ((this.useIA || this.useSecurityMode) && basePath) {
      const segments = basePath.split('/');

      let jpgIndex = -1;
      for (let i = segments.length - 1; i >= 0; i--) {
        if (segments[i].toLowerCase().includes('.jpg')) {
          jpgIndex = i;
          break;
        }
      }

      if (jpgIndex !== -1) {
        segments.splice(jpgIndex, 0, 'IA');
        if (this.useSecurityMode) {
          segments.splice(jpgIndex + 1, 0, 'Segmentation');
        }
        basePath = segments.join('/');
      } else {
        basePath += '/IA';
        if (this.useSecurityMode) {
          basePath += '/Segmentation';
        }
      }
    }

    return basePath;
  }

  public get isIaPage(): boolean {
    return this.$route.name === 'ia'; // Remplacez 'IA' par le nom réel de votre route IA
  }

  @Watch('isLoadingPhoto')
  public onLoadingPhotoChanged(newValue: boolean): void {
    if (!newValue) {
      this.$nextTick(() => {
        const zoomerElement = this.$el.querySelector('.gallery-photo-preview-container') as HTMLElement;
        if (zoomerElement) {
          zoomerElement.addEventListener('wheel', this.onWheelZoom);
        } else {
          console.log('zoomerElement not found using querySelector');
        }
      });
    }
  }

  @Watch('currentImageKey')
  public onCurrentImageKeyChange(newVal: Photo | null, oldVal: Photo | null): void {
    if (newVal !== null) {
      this.$emit('selectedPhoto', newVal);
    }
  }

  public onWheelZoom(event: WheelEvent): void {
    let zoomStep = 0.2; // Incrément de zoom pour la molette de la souris
    const isTrackpad = Math.abs(event.deltaY) < 15 && event.deltaMode === 0; // Détecte les petits incréments typiques du pavé tactile

    if (isTrackpad) {
      zoomStep = 0.002; // Réduire l'incrément de zoom pour le pavé tactile
    }
    if (event.deltaY < 0) {
      this.zoomLevel += zoomStep;
    } else {
      this.zoomLevel -= zoomStep;
    }
    if (this.zoomLevel < 1) {
      this.zoomLevel = 1;
    }
  }

  public async onCopyImage(): Promise<void> {
    if (this.currentImageKey) {
      try {
        const sourceKey = this.currentImageKey.key; // Chemin de l'image actuelle
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const fileExtension = sourceKey.split('.').pop()?.split('?')[0] || 'jpg';
        // Génération d'un nom de fichier pour l'image uploadée sur Roboflow
        const fileName = `copied-${timestamp}.${fileExtension}`;

        // Télécharger l'image depuis S3
        const response = (await Storage.get(sourceKey, { download: true })) as S3GetResponse;
        const body = response.Body;

        // Vérifier si Body est un Blob ou un ArrayBuffer
        if (body instanceof Blob || body instanceof ArrayBuffer) {
          const imageBlob = body instanceof Blob ? body : new Blob([body], { type: `image/${fileExtension}` });

          // Créer un FormData pour l'envoi vers Roboflow
          const formData = new FormData();
          formData.append('name', fileName);
          formData.append('file', imageBlob, fileName);
          formData.append('split', 'train'); // Modifier si nécessaire (ex: "valid", "test", ...)

          // Configuration Roboflow (remplacez par vos valeurs)
          const ROBOFLOW_API_KEY = 'sMvFYAUEXICXjfJaPDAs';
          const ROBOFLOW_DATASET = this.useIA
            ? 'excavation-2'
            : this.useSecurityMode
              ? 'excavation-2'
              : 'excavation-2';

          // Envoi de la requête POST à Roboflow
          const axiosResponse = await axios.post(
            `https://api.roboflow.com/dataset/${ROBOFLOW_DATASET}/upload`,
            formData,
            {
              params: {
                api_key: ROBOFLOW_API_KEY,
              },
              // En environnement navigateur, axios gère automatiquement l'en-tête multipart/form-data
            }
          );

          console.log('Réponse Roboflow :', axiosResponse.data);
          this.$bvToast.toast(this.$t('gallery.copySuccess').toString(), {
            title: 'Succès',
            variant: 'success',
            solid: true,
          });
        } else {
          throw new Error("Le fichier téléchargé n'est pas un Blob ou un ArrayBuffer.");
        }
      } catch (error) {
        // On récupère la chaîne de l'erreur pour voir si elle contient "Network Error"
        const errorStr = error && error.toString ? error.toString() : '';
        if (errorStr.includes('Network Error')) {
          console.log("Network Error détecté, mais l'upload semble réussi d'après les logs réseau.");
          this.$bvToast.toast(this.$t('gallery.copySuccess').toString(), {
            title: 'Succès',
            variant: 'success',
            solid: true,
          });
          try {
            await axios.post('https://ncv8s6bnc5.execute-api.eu-west-3.amazonaws.com/prod/send-email', {});
            console.log('Email de notification envoyé via Lambda.');
          } catch (emailError) {
            console.error("Erreur lors de l'envoi de l'email :", emailError);
          }
        } else {
          console.error("Erreur lors de l'upload de l'image vers Roboflow :", error);
          this.$bvToast.toast(this.$t('gallery.copyError').toString(), {
            title: 'Erreur',
            variant: 'danger',
            solid: true,
          });
        }
      }
    } else {
      console.warn("Aucune image sélectionnée pour l'upload.");
    }
  }

  public onDoubleClickZoom(): void {
    if (this.zoomLevel < 3) {
      this.zoomLevel = 3;
    } else {
      this.zoomLevel = 1;
    }
  }

  mounted(): void {
    logger.info('mounted');
    document.addEventListener('keyup', this.onNextSlide);
    if (this.zoom) {
      document.addEventListener('keyup', this.onNextSlide);
    }

    if (!this.isLoadingPhoto) {
      this.$nextTick(() => {
        const zoomerElement = this.$el.querySelector('.gallery-photo-preview-container') as HTMLElement;
        if (zoomerElement) {
          zoomerElement.addEventListener('wheel', this.onWheelZoom);
        } else {
          console.log('zoomerElement not found using querySelector');
        }
      });
    }
  }

  private filterImagesWithinLagDuration(images: Photo[], lagduration: number): Photo[] {
    const now = new Date();
    const cutoffTime = new Date(now.getTime() - lagduration * 60 * 60 * 1000); // lagduration hours ago
    return images.filter((image) => new Date(image.createdAt) < cutoffTime);
  }

  public getRotation(): string {
    return this.camera && this.camera.rotate ? `rotate(${this.camera.rotate}deg)` : '';
  }

  public togglDirection(mode: 'r' | 'b' | 's'): void {
    this.$emit('switchmode', mode);
    if (this.$refs.galleryCompare) {
      (this.$refs.galleryCompare as any).resetImages();
    }
  }

  public takePicture(): void {
    cameraService.takePicture(this.camera.id);
  }

  public async handleTakePicture(): Promise<void> {
    this.isTakingPicture = true; // Activer le loader
    try {
      await cameraService.takePicture(this.camera.id); // Appeler le service pour prendre une photo
      this.waitForPhotoNotification(); // Attendre la notification (ou une promesse)
    } catch (error) {
      console.error('Erreur lors de la prise de photo :', error);
      this.isTakingPicture = false; // Désactiver le loader en cas d'erreur
    }
  }

  public async handleMiroring(): Promise<void> {
    // Pour éviter que l'utilisateur ne reclique pendant la phase "spinner"
    //if (this.isMirroring) return;

    this.isMirroring = true; // Active le spinner

    try {
      // Appel à votre service (remplacez par votre logique réelle)
      await cameraService.Miroring(this.camera.id);

      // Une fois la requête effectuée, on laisse le spinner pendant 8 secondes
      setTimeout(() => {
        // On arrête le spinner
        this.isMirroring = false;
        // On affiche l'icône de validation
        this.mirroringSuccess = true;

        // Puis 2 secondes plus tard, on remet l'icône de base
        setTimeout(() => {
          this.mirroringSuccess = false;
        }, 2000);
      }, 5000);
    } catch (error) {
      console.error('Erreur lors du miroring :', error);
      // Si erreur, on désactive le spinner
      this.isMirroring = false;
      // On peut éventuellement afficher une notification d'erreur ici
    }
  }

  private waitForPhotoNotification(): void {
    const timeout = setTimeout(() => {
      clearInterval(checkPhotoInterval);
      this.isTakingPicture = false; // Désactiver le loader après un délai
    }, 30000); // Timeout après 15 secondes

    const checkPhotoInterval = setInterval(() => {
      if (this.newPhotoAvailable) {
        clearTimeout(timeout); // Annuler le timeout
        clearInterval(checkPhotoInterval); // Arrêter l'intervalle
        this.isTakingPicture = false; // Désactiver le loader
      }
    }, 500);
  }

  public refreshPhoto(): void {
    // eslint-disable-next-line vue/custom-event-name-casing
    this.$emit('refreshPhoto');
  }

  @Watch('newPhotoAvailable')
  onNewPhotoAvailableChanged(newVal: boolean): void {
    if (newVal) {
      setTimeout(() => {
        this.refreshPhoto();
      }, 2000);
    }
  }

  @Watch('pickedDate')
  public onDatePick(val: Date, oldVal: Date | null): void {
    logger.info('pickedDate', { val, oldVal });
    if (oldVal !== null) {
      let closestPhoto: Photo | null = null;
      let closestDistance = Infinity;

      for (const imageKey of this.imagesKey) {
        const photoDate = new Date(imageKey.createdAt);
        photoDate.setHours(photoDate.getHours() + photoDate.getTimezoneOffset() / 60);
        const localDistance = Math.abs(val.getTime() - photoDate.getTime());
        if (localDistance < closestDistance) {
          closestDistance = localDistance;
          closestPhoto = imageKey;
        }
      }

      if (closestPhoto) {
        this.currentImageKey = closestPhoto;
        this.sliderValue = this.currentImageKey;
        this.$emit('photoChanged', new Date(closestPhoto.createdAt)); // Émission de l'événement
      } else {
        alert('Aucune image trouvée.');
      }
    }
  }

  @Watch('imagesKey')
  onImagesKeyChange(newVal: Photo[]) {
    this.currentImageKey = newVal.length > 0 ? newVal[newVal.length - 1] : null;
    this.sliderValue = this.currentImageKey;

    // Mettre à jour le pickedDate si nécessaire
    if (this.currentImageKey && this.pickedDate === null) {
      this.pickedDate = new Date(this.currentImageKey.createdAt);
      this.pickedDate.setHours(this.pickedDate.getHours() + this.pickedDate.getTimezoneOffset() / 60);
      this.pickedDate.setHours(12);
      this.pickedDate.setMinutes(0);
      this.pickedDate.setSeconds(0);
    }

    // Gérer le zoom initial pour les Tikee
    if (this.camera.isTikee === true && this.currentImageKey !== null) {
      const zoomLevel = this.camera.optic !== '4260x1534' ? 1 : 1.56;
      this.$nextTick(() => {
        // @ts-ignore no typescript def for $refs
        this.$refs.zoomerMedium?.reset();
        // @ts-ignore no typescript def for $refs
        this.$refs.zoomerMedium?.zoomIn(zoomLevel);
      });
    }

    // Recalage du slider à la dernière photo
    this.$nextTick(() => {
      if (this.slider && newVal.length > 0) {
        this.slider.setIndex(newVal.length - 1);
      }
    });
  }

  @Watch('selectedPhotos', { immediate: true, deep: true })
  public onSelectedPhotosChange(newVal: Photo[]): void {
    if (newVal && newVal.length > 0) {
      const selectedPhoto = newVal[0]; // Supposons une sélection unique
      this.currentImageKey = selectedPhoto;

      // Trouver l'index de la photo sélectionnée dans imagesKey
      const index = this.imagesKey.findIndex((photo) => photo.id === selectedPhoto.id);

      if (index !== -1 && this.slider) {
        this.slider.setIndex(index);
        this.sliderValue = selectedPhoto;
      }
    } else {
      // Si aucune photo n'est sélectionnée, réinitialiser à la dernière photo
      this.currentImageKey = this.imagesKey.length > 0 ? this.imagesKey[this.imagesKey.length - 1] : null;
      this.sliderValue = this.currentImageKey;
    }
  }

  public get sliderData(): string[] {
    return this.imagesKey.map((imageKey) => imageKey.smallKey);
  }

  public onSliderChange(value: Photo): void {
    if (!this.sliderIsDragging) {
      this.currentImageKey = value;
      const photoDate = new Date(value.createdAt);
      console.log('Emitting photoChanged with date:', photoDate);
      this.$emit('photoChanged', photoDate);
    }
    this.isSliderFirstItem = this.slider.getIndex() === 0;
    this.isSliderLastItem = this.slider.getIndex() === this.imagesKey.length - 1;
  }

  public goToClosestPhoto(date: Date): void {
    console.log('goToClosestPhoto called with date:', date);
    if (this.imagesKey.length === 0) return;
    let closestPhoto: Photo = this.imagesKey[0];
    let closestDiff = Math.abs(new Date(closestPhoto.createdAt).getTime() - date.getTime());

    for (const photo of this.imagesKey) {
      const diff = Math.abs(new Date(photo.createdAt).getTime() - date.getTime());
      if (diff < closestDiff) {
        closestDiff = diff;
        closestPhoto = photo;
      }
    }

    console.log('Found closest photo:', closestPhoto);
    this.currentImageKey = closestPhoto;
    const index = this.imagesKey.findIndex((p) => p.id === closestPhoto.id);
    if (index !== -1 && this.slider) {
      this.slider.setIndex(index);
      this.sliderValue = closestPhoto;
    } else {
      console.warn('Slider or photo index not found');
    }
  }

  public onSliderDragStart(): void {
    this.sliderIsDragging = true;
  }

  public onSliderDragEnd(): void {
    this.sliderIsDragging = false;
    this.currentImageKey = this.sliderValue;

    if (this.sliderValue) {
      const date = new Date(this.sliderValue.createdAt);
      this.goToClosestPhoto(date);
      this.$emit('photoChanged', date); // Émission de l'événement
    }
  }

  public onNextSlide(event: KeyboardEvent): void {
    if (event.keyCode === 37) {
      this.onPrevImageClick();
    } else if (event.keyCode === 39) {
      this.onNextImageClick();
    } else if (event.keyCode === 27 && this.zoom) {
      this.zoom = false;
    }
  }

  public onPrevImageClick(): void {
    if (this.slider) {
      const index = this.slider.getIndex() as number;
      if (index >= 1) {
        this.slider.setIndex(index - 1);
      } else {
        this.slider.setIndex(0);
      }
    }
  }

  public onNextImageClick(): void {
    if (this.slider) {
      const index = this.slider.getIndex() as number;
      if (index < this.imagesKey.length - 1) {
        this.slider.setIndex(index + 1);
      } else {
        this.slider.setIndex(this.imagesKey.length - 1);
      }
    }
  }

  public onshow(): void {
    logger.info('onShow');
    this.showCreateAlbum();
    this.showSelectAlbum();
    // eslint-disable-next-line vue/custom-event-name-casing
    this.$root.$emit('bv::show::popover', 'popover-favorite');
  }

  public onClose(): void {
    this.showCreateAlbum();
    this.showSelectAlbum();
    // eslint-disable-next-line vue/custom-event-name-casing
    this.$root.$emit('bv::hide::popover', 'popover-favorite');
  }

  public onDownload(): void {
    if (this.currentImageKey !== null) {
      const imageKey = this.currentImageKey;
      Storage.get(imageKey.key, { download: true })
        .then((response) => {
          saveAs(
            // @ts-ignore amplify no doc
            new Blob([response.Body], { type: 'image/jpeg' }),
            `${format(new Date(imageKey.createdAt), 'yyyyMMdd-HHmmss')}.jpg`
          );
        })
        .catch((error) => {
          console.error("Erreur lors du téléchargement de l'image:", error);
        });
    } else {
      console.warn('Aucune image sélectionnée pour le téléchargement.');
    }
  }

  public showCreateAlbum(): void {
    logger.info('showCreateAlbum');
    this.$v.selectedAlbum.$reset();
    this.selectedAlbum = '';
    this.photoFavAlbum = !this.photoFavAlbum;
  }

  public showSelectAlbum(): void {
    logger.info('showSelectAlbum');
    this.$v.createdAlbum.$reset();
    this.createdAlbum = '';
    this.photoFavAlbum = !this.photoFavAlbum;
  }

  private async onSelectAlbum(albumId: string) {
    logger.info('onSelectAlbum ' + albumId);
    this.$v.selectedAlbum.$touch();
    if (!this.$v.selectedAlbum.$invalid && this.currentImageKey !== null) {
      await this.addPhoto({ albumId, photoId: this.currentImageKey.id });
      this.onClose();
    }
  }

  private async onCreateAlbum(albumName: string) {
    logger.info('onCreateAlbum ' + albumName);
    this.$v.createdAlbum.$touch();
    if (!this.$v.createdAlbum.$invalid && this.currentImageKey !== null) {
      this.createNewAlbum({
        cameraId: this.camera.id,
        projectId: this.project.id,
        name: albumName,
        photoId: this.currentImageKey.id,
      });
      this.onClose();
    }
  }
}
